/**
 * The listeners that fill the feedback context ring (SA-1304). Installed once from main.tsx,
 * whatever the feature flags say: a few listeners and at most 50 small events, nothing sent.
 *
 * What is read: a control's accessible name when it is clicked, a field's label when it changes
 * (never its value), the page's path template and title, errors, and each API response's method,
 * path template, status and refusal code. Nothing inside `[data-feedback-private]` is read.
 */
import { setResponseObserver, type ResponseReport } from '@/services/apiClient'
import { useFeedbackContextStore } from '@/stores/feedbackContextStore'
import { cleanQuery } from './allowlist'
import { templatePath } from './paths'

export type ErrorSource = 'window' | 'rejection' | 'console' | 'boundary' | 'toast' | 'refusal'

const PRIVATE = '[data-feedback-private]'
const CONTROL =
  'button, a[href], [role="button"], [role="link"], [role="tab"], [role="menuitem"], [role="option"], [role="checkbox"], [role="switch"]'
const ROLES = new Set(['button', 'link', 'tab', 'menuitem', 'option', 'checkbox', 'switch'])
/** How long after a navigation the page's title is read: lazy pages set it in an effect. */
export const TITLE_DELAY_MS = 300

function record(kind: string, raw: Record<string, unknown>) {
  useFeedbackContextStore.getState().record(kind, raw)
}

function textOf(el: Element | null): string {
  return (el?.textContent ?? '').replace(/\s+/g, ' ').trim()
}

function labelledBy(el: Element): string {
  const ids = el.getAttribute('aria-labelledby')
  if (!ids) return ''
  return ids
    .split(/\s+/)
    .map((id) => textOf(el.ownerDocument.getElementById(id)))
    .filter(Boolean)
    .join(' ')
}

/** A control's accessible name: aria-label, aria-labelledby, then its text. */
export function controlName(el: Element): string {
  return (el.getAttribute('aria-label') ?? '').trim() || labelledBy(el) || textOf(el) || (el.getAttribute('title') ?? '').trim()
}

/** A field's label, never its value: aria-label, aria-labelledby, then its <label>. */
export function fieldLabel(el: Element): string {
  const aria = (el.getAttribute('aria-label') ?? '').trim() || labelledBy(el)
  if (aria) return aria
  const labels = (el as HTMLInputElement).labels
  if (labels && labels.length > 0) return textOf(labels[0])
  return ''
}

function roleOf(el: Element): string | undefined {
  const explicit = el.getAttribute('role')
  if (explicit && ROLES.has(explicit)) return explicit
  if (el.tagName === 'A') return 'link'
  if (el.tagName === 'BUTTON') return 'button'
  return undefined
}

function onClick(event: MouseEvent) {
  const target = event.target
  if (!(target instanceof Element) || target.closest(PRIVATE)) return
  const control = target.closest(CONTROL)
  if (!control) return
  const name = controlName(control)
  if (name) record('click', { name, role: roleOf(control) })
}

function onChange(event: Event) {
  const target = event.target
  if (!(target instanceof Element) || target.closest(PRIVATE)) return
  if (!target.matches('input, select, textarea')) return
  const field = fieldLabel(target)
  if (field) record('change', { field })
}

function messageOf(value: unknown): string {
  if (value instanceof Error) return value.message
  if (typeof value === 'string') return value
  return ''
}

/** One error line; anything that is not a string or an Error is left out, never stringified. */
export function recordError(source: ErrorSource, message: string, code?: string) {
  record('error', { source, message, code })
}

/** A refusal the browser makes itself (a server's refusal is already an `api` event). */
/** A page-tour event (SA-1315): ids and a step number, never text. */
export function recordTour(action: 'start' | 'step' | 'missing' | 'exit' | 'done', tour: string, step?: number) {
  record('tour', step === undefined ? { action, tour } : { action, tour, step })
}

export function recordRefusal(code: string, text: string) {
  recordError('refusal', text, code)
}

function onWindowError(event: ErrorEvent) {
  recordError('window', messageOf(event.error) || event.message)
}

function onRejection(event: PromiseRejectionEvent) {
  recordError('rejection', messageOf(event.reason))
}

function onResponse(report: ResponseReport) {
  if (!report.sameIdentity) return
  record('api', {
    method: report.method,
    path: templatePath(report.path).template,
    status: report.status,
    ms: report.ms,
    code: report.code,
  })
}

export interface NavigationLocation {
  pathname: string
  search: string
}

let pendingNav: { location: NavigationLocation; timer: ReturnType<typeof setTimeout> } | null = null

function flushNavigation() {
  if (!pendingNav) return
  const { location, timer } = pendingNav
  clearTimeout(timer)
  pendingNav = null
  const { template, params } = templatePath(location.pathname)
  record('nav', { path: template, params, query: cleanQuery(location.search), title: document.title })
}

/** A navigation; recorded once the new page has had time to set its title. */
export function recordNavigation(location: NavigationLocation) {
  flushNavigation()
  pendingNav = { location, timer: setTimeout(flushNavigation, TITLE_DELAY_MS) }
}

/** What main.tsx's data router offers, kept narrow so tests need no router. */
export interface NavigationSource {
  state: { location: NavigationLocation }
  subscribe(listener: (state: { location: NavigationLocation }) => void): () => void
}

let installed: (() => void) | null = null
let consoleQuiet = false

/** Run `fn` with console.error passed through but not recorded (a caller that records its own line). */
export function quietly(fn: () => void) {
  const was = consoleQuiet
  consoleQuiet = true
  try {
    fn()
  } finally {
    consoleQuiet = was
  }
}

/** Install every listener once; returns the uninstall (a second install returns the first's). */
export function installFeedbackContext(navigation?: NavigationSource): () => void {
  if (installed) return installed

  document.addEventListener('click', onClick, true)
  document.addEventListener('change', onChange, true)
  window.addEventListener('error', onWindowError)
  window.addEventListener('unhandledrejection', onRejection)
  setResponseObserver(onResponse)

  const originalError = console.error
  console.error = (...args: unknown[]) => {
    originalError.apply(console, args)
    if (consoleQuiet) return
    quietly(() => {
      const message = args.map(messageOf).filter(Boolean).join(' ')
      if (message) recordError('console', message)
    })
  }

  let lastPath: string | null = null
  const onLocation = (location: NavigationLocation) => {
    const key = `${location.pathname}${location.search}`
    if (key === lastPath) return
    lastPath = key
    recordNavigation(location)
  }
  let unsubscribe: (() => void) | null = null
  if (navigation) {
    onLocation(navigation.state.location)
    unsubscribe = navigation.subscribe((state) => onLocation(state.location))
  }

  installed = () => {
    document.removeEventListener('click', onClick, true)
    document.removeEventListener('change', onChange, true)
    window.removeEventListener('error', onWindowError)
    window.removeEventListener('unhandledrejection', onRejection)
    setResponseObserver(null)
    console.error = originalError
    unsubscribe?.()
    if (pendingNav) clearTimeout(pendingNav.timer)
    pendingNav = null
    installed = null
  }
  return installed
}
